import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Button, BackButton, Modal, ModalContent, LoadingState } from '../components/common';
import MobileSearchField from '../components/common/MobileSearchField';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { ClubCard } from '../components/clubs';
import ClubInfoModal from '../components/clubs/ClubInfoModal';
import {
  fetchHomeMemberships,
  fetchHomeClubsSnapshot,
  fetchHomeClubsWinRates,
} from '../services/homeDashboardService';
import { 
  checkForGuestAccountsAtClub, 
  linkGuestAccountBeforeClubJoin,
  validateClubJoinEligibility,
  createEmailAssignmentRequest,
  startFreshWithoutGuestMerge,
  declineGuestMergeWithReport,
  getMyPendingEmailAssignmentClubIds,
  suggestGuestNameMatchesForJoin,
} from '../services/guestAccountLinkingService';
import StartFreshWarningModal from '../components/guest/StartFreshWarningModal';
import DeclineGuestMergeModal from '../components/guest/DeclineGuestMergeModal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import GuestLinkActionChooser from '../components/guest/GuestLinkActionChooser';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import { getMyClubJoinRequests } from '../services/clubJoinRequestService';
import {
  requestClubReactivation,
  withdrawClubReactivation,
} from '../services/clubMembershipService';
import { useToast } from '../context/ToastContext';
import {
  getDiscoverClubStatus,
  isJoinBlockedStatus,
  mapJoinRequestsByClub,
} from '../utils/clubJoinStatus';

/** At most one guest per club+email in normal admin flow; auto-select when only one match. */
function resolveSelectedGuestId(guests, preferredId = null) {
  const list = guests || [];
  if (list.length === 0) return null;
  if (list.length === 1) return list[0].id;
  if (preferredId && list.some((g) => g.id === preferredId)) return preferredId;
  return null;
}

function ModalInfoTooltip({ text }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        onBlur={() => setOpen(false)}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-muted hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-label="More information"
        aria-expanded={open}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
      {open ? (
        <div
          role="tooltip"
          className="absolute left-1/2 bottom-full z-10 mb-2 w-64 -translate-x-1/2 rounded-lg border border-border bg-surface-elevated px-3 py-2 text-left text-xs leading-relaxed text-text-secondary shadow-brand-md"
        >
          {text}
        </div>
      ) : null}
    </div>
  );
}

const Clubs = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userMemberships, setUserMemberships] = useState({});
  const [userJoinRequests, setUserJoinRequests] = useState({});
  const [userJoinRequestIds, setUserJoinRequestIds] = useState({}); // Store request IDs for withdrawal
  const [userJoinRequestCooldowns, setUserJoinRequestCooldowns] = useState({}); // Store cooldown info: { clubId: { cooldown_until, is_blocked, message } }
  const [withdrawingRequest, setWithdrawingRequest] = useState(null);
  const [userEmailAssignmentRequests, setUserEmailAssignmentRequests] = useState({});
  const [clubActivity, setClubActivity] = useState({}); // Store session activity per club
  const [memberClubs, setMemberClubs] = useState([]);
  const [memberClubsLoading, setMemberClubsLoading] = useState(false);
  const [memberInsightsLoading, setMemberInsightsLoading] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showClubInfoModal, setShowClubInfoModal] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [joinSuccess, setJoinSuccess] = useState(null);
  
  // Identity gate state
  const [hasPlayedAsGuest, setHasPlayedAsGuest] = useState(null); // null = not asked, true/false = answered
  const [guestAccounts, setGuestAccounts] = useState([]);
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [mergingGuest, setMergingGuest] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showStartFreshModal, setShowStartFreshModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showMergeConfirmModal, setShowMergeConfirmModal] = useState(false);
  const [expandedGuests, setExpandedGuests] = useState({}); // Track which guests have expanded details
  const [guestNameHints, setGuestNameHints] = useState([]);
  const [dismissedGuestNameHint, setDismissedGuestNameHint] = useState(false);
  
  // Notification-triggered merge state
  const [notificationMergeGuestId, setNotificationMergeGuestId] = useState(null);

  const resetJoinModalState = () => {
    setShowJoinModal(false);
    setSelectedClub(null);
    setHasPlayedAsGuest(null);
    setGuestAccounts([]);
    setSelectedGuestId(null);
    setJoinMessage('');
    setJoinError(null);
    setJoinSuccess(null);
    setNotificationMergeGuestId(null);
    setExpandedGuests({});
    setGuestNameHints([]);
    setDismissedGuestNameHint(false);
  };

  useEffect(() => {
    fetchAllClubs();
    if (currentUser?.id) {
      fetchUserMemberships();
      fetchUserJoinRequests();
      fetchUserEmailAssignmentRequests();
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) {
      setMemberClubs([]);
      setMemberInsightsLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setMemberClubsLoading(true);
      setMemberInsightsLoading(true);
      try {
        const memberships = await fetchHomeMemberships(currentUser.id, { includeInactive: true });
        const snapshot = await fetchHomeClubsSnapshot(memberships, currentUser.id, { limit: null });
        if (cancelled) return;

        setMemberClubs(snapshot.clubs || []);
        setMemberClubsLoading(false);

        const withWinRates = await fetchHomeClubsWinRates(snapshot, currentUser.id);
        if (!cancelled) {
          setMemberClubs(withWinRates.clubs || []);
        }
      } catch (err) {
        console.error('Error loading member clubs:', err);
        if (!cancelled) setMemberClubs([]);
      } finally {
        if (!cancelled) {
          setMemberClubsLoading(false);
          setMemberInsightsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  // Fetch club activity when clubs are loaded
  useEffect(() => {
    if (clubs.length > 0) {
      fetchClubActivity();
    }
  }, [clubs]);

  // Handle notification-triggered merge flow
  useEffect(() => {
    if (location.state?.mergeGuestId && location.state?.mergeClubId && clubs.length > 0 && currentUser?.id) {
      const mergeClubId = location.state.mergeClubId;
      const mergeGuestId = location.state.mergeGuestId;
      
      // Find the club
      const club = clubs.find(c => c.id === mergeClubId);
      if (club) {
        setSelectedClub(club);
        setNotificationMergeGuestId(mergeGuestId);
        setSelectedGuestId(mergeGuestId);
        setHasPlayedAsGuest(true); // Skip identity gate, we know they have a guest account
        setShowJoinModal(true);
        
        // Load guest accounts for this club (will pre-select the notification guest)
        setLoadingGuests(true);
        checkForGuestAccountsAtClub(currentUser.id, mergeClubId)
          .then((guests) => {
            const list = guests || [];
            setGuestAccounts(list);
            setSelectedGuestId(resolveSelectedGuestId(list, mergeGuestId));
          })
          .catch(err => {
            console.error('Error loading guest accounts:', err);
            setJoinError('Failed to load guest account details');
          })
          .finally(() => {
            setLoadingGuests(false);
          });
        
        // Clear the state from location to prevent re-triggering
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, clubs, navigate, currentUser?.id]);


  const fetchAllClubs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .rpc('search_public_clubs', {
          search_term: '',
          limit_count: 100,
          offset_count: 0
        });

      if (error) throw error;
      
      // Fetch club admins for each club
      if (data && data.length > 0) {
        const clubIds = data.map(club => club.id);
        const { data: adminsData, error: adminsError } = await supabase
          .from('players_club')
          .select(`
            club_id,
            player:players(
              id,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .in('club_id', clubIds)
          .in('club_role', ['owner', 'admin'])
          .limit(3); // Get up to 3 admins per club
        
        if (!adminsError && adminsData) {
          // Group admins by club_id
          const adminsByClub = {};
          adminsData.forEach(row => {
            if (!adminsByClub[row.club_id]) {
              adminsByClub[row.club_id] = [];
            }
            if (row.player && adminsByClub[row.club_id].length < 3) {
              adminsByClub[row.club_id].push(row.player);
            }
          });
          
          // Add admins to club objects
          const clubsWithAdmins = data.map(club => ({
            ...club,
            admins: adminsByClub[club.id] || []
          }));
          
          setClubs(clubsWithAdmins);
        } else {
          setClubs(data || []);
        }
      } else {
        setClubs(data || []);
      }
    } catch (err) {
      console.error('Error fetching all clubs:', err);
      setError('Failed to load clubs.');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubActivity = async () => {
    try {
      const clubIds = clubs.map(club => club.id);
      if (clubIds.length === 0) return;

      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      // Fetch all sessions for these clubs in one query
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('sessions')
        .select('id, club_id, session_date, start_time, name, session_status')
        .in('club_id', clubIds)
        .gte('session_date', sevenDaysAgo.toISOString().split('T')[0])
        .lte('session_date', sevenDaysFromNow.toISOString().split('T')[0])
        .in('session_status', ['upcoming', 'ongoing', 'completed'])
        .order('session_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (sessionsError) {
        console.error('Error fetching club activity:', sessionsError);
        return;
      }

      // Process data per club
      const activityByClub = {};
      
      clubs.forEach(club => {
        const clubSessions = sessionsData?.filter(s => s.club_id === club.id) || [];
        
        // Sessions in the past 7 days (for "active" badge)
        const recentSessions = clubSessions.filter(s => {
          const sessionDate = new Date(s.session_date);
          return sessionDate >= sevenDaysAgo && sessionDate <= now;
        });

        // Upcoming sessions in next 7 days
        const upcomingSessions = clubSessions.filter(s => {
          const sessionDate = new Date(s.session_date);
          return sessionDate > now && sessionDate <= sevenDaysFromNow;
        });

        // Find next session
        const nextSession = upcomingSessions.length > 0 ? upcomingSessions[0] : null;

        activityByClub[club.id] = {
          hasRecentActivity: recentSessions.length > 0,
          upcomingCount: upcomingSessions.length,
          nextSession: nextSession ? {
            date: nextSession.session_date,
            time: nextSession.start_time,
            name: nextSession.name
          } : null
        };
      });

      setClubActivity(activityByClub);
    } catch (err) {
      console.error('Error processing club activity:', err);
    }
  };

  const fetchUserMemberships = async () => {
    if (!currentUser?.id) return;
    
    try {
      const { data, error} = await supabase
        .from('players_club')
        .select('club_id, club_role, is_active, membership_status, reactivation_requested_at')
        .eq('player_id', currentUser.id)
        .neq('club_role', 'guest')
        .or('is_active.eq.true,membership_status.eq.inactive');

      if (error) throw error;

      const memberships = {};
      data?.forEach(membership => {
        memberships[membership.club_id] = {
          role: membership.club_role,
          isActive: membership.is_active && membership.membership_status !== 'inactive',
          membershipStatus: membership.membership_status || 'active',
          reactivationRequestedAt: membership.reactivation_requested_at || null,
        };
      });

      setUserMemberships(memberships);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    }
  };

  const fetchUserJoinRequests = async () => {
    if (!currentUser?.id) return;
    
    try {
      const data = await getMyClubJoinRequests();
      const { requests, requestIds, cooldowns } = mapJoinRequestsByClub(data);
      setUserJoinRequests(requests);
      setUserJoinRequestIds(requestIds);
      setUserJoinRequestCooldowns(cooldowns);
    } catch (err) {
      console.error('Error fetching join requests:', err);
    }
  };

  const fetchUserEmailAssignmentRequests = async () => {
    if (!currentUser?.id) return;
    
    try {
      const clubIds = await getMyPendingEmailAssignmentClubIds();
      const requests = {};
      clubIds.forEach((clubId) => {
        requests[clubId] = true;
      });
      setUserEmailAssignmentRequests(requests);
    } catch (err) {
      console.error('Error fetching email assignment requests:', err);
    }
  };

  const getUserClubStatus = (clubId) =>
    getDiscoverClubStatus({
      isLoggedIn: Boolean(currentUser),
      membership: userMemberships[clubId],
      joinRequest: userJoinRequests[clubId],
      emailPending: Boolean(userEmailAssignmentRequests[clubId]),
      cooldownInfo: userJoinRequestCooldowns[clubId],
    });

  const handleClubClick = (club) => {
    // Only navigate if user is a member
    const status = currentUser ? getUserClubStatus(club.id) : null;
    if (status?.status === 'member') {
      navigate(`/clubs/${club.id}`);
    }
  };

  const handleViewClub = (club) => {
    setSelectedClub(club);
    setShowClubInfoModal(true);
  };

  const handleRequestJoin = async (club) => {
    if (!currentUser?.id) {
      // Redirect to login or show message
      return;
    }
    const existingStatus = getUserClubStatus(club.id);
    if (isJoinBlockedStatus(existingStatus?.status)) {
      return;
    }
    setSelectedClub(club);
    setHasPlayedAsGuest(null); // Reset identity gate
    setGuestAccounts([]);
    setSelectedGuestId(null);
    setJoinMessage('');
    setJoinError(null);
    setJoinSuccess(null);
    setGuestNameHints([]);
    setDismissedGuestNameHint(false);
    setLoadingGuests(true);
    setShowJoinModal(true);
    
    // Immediately scan for guest accounts
    try {
      const guests = await checkForGuestAccountsAtClub(currentUser.id, club.id);
      
      if (guests && guests.length > 0) {
        setGuestAccounts(guests);
        setSelectedGuestId(resolveSelectedGuestId(guests));
        setHasPlayedAsGuest(true); // Skip the initial question, go straight to guest selection
      }
      // No guest accounts found — stay at null to show the Yes/No identity gate
    } catch (err) {
      console.error('Error checking for guest accounts:', err);
      setJoinError('Failed to check for guest accounts. Please try again.');
      // Still show the modal with the identity gate
      setHasPlayedAsGuest(null);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleIdentityGateAnswer = async (answeredYes) => {
    setHasPlayedAsGuest(answeredYes);
    
    if (answeredYes) {
      // Check for guest accounts at this club
      setLoadingGuests(true);
      try {
        const guests = await checkForGuestAccountsAtClub(currentUser.id, selectedClub.id);
        const list = guests || [];
        setGuestAccounts(list);
        setSelectedGuestId(resolveSelectedGuestId(list, notificationMergeGuestId));

        // If no guests found, user will see a button to request email assignment
        if (guests.length === 0) {
          setJoinError(null); // Clear any previous errors
        }
      } catch (err) {
        console.error('Error checking for guest accounts:', err);
        setJoinError('Failed to check for guest accounts. Please try again.');
      } finally {
        setLoadingGuests(false);
      }
      return;
    }

    setLoadingGuests(true);
    setDismissedGuestNameHint(false);
    setJoinError(null);
    try {
      const hints = await suggestGuestNameMatchesForJoin(selectedClub.id);
      setGuestNameHints(hints || []);
    } catch (err) {
      console.error('Error checking for similar guest names:', err);
      setGuestNameHints([]);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleSendEmailAssignmentRequest = async () => {
    if (!currentUser?.id || !selectedClub?.id) {
      setJoinError('Unable to send request. Please try again.');
      return;
    }

    try {
      setSendingRequest(true);
      setJoinError(null);
      setJoinSuccess(null);

      await createEmailAssignmentRequest(currentUser.id, selectedClub.id);

      setUserEmailAssignmentRequests((prev) => ({
        ...prev,
        [selectedClub.id]: true,
      }));
      
      setJoinSuccess('Request sent! We\'ve notified the club admins to link your previous account. You\'ll receive a notification when it\'s ready to connect.');
    } catch (err) {
      console.error('Error creating email assignment request:', err);
      setJoinError(err.message || 'Failed to send request. Please try again.');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleGuestSelectionAndMerge = async () => {
    if (!selectedGuestId) {
      setJoinError('Please select a guest account to link.');
      return;
    }

    try {
      setMergingGuest(true);
      setJoinError(null);

      // Merge guest account and join club in one transaction
      const result = await linkGuestAccountBeforeClubJoin(
        selectedGuestId,
        currentUser.id,
        selectedClub.id
      );

      if (result.status === 'merged' || result.club_joined) {
        setJoinSuccess('Guest account linked and club joined successfully!');
        // Refresh data
        await fetchUserMemberships();
        await fetchUserJoinRequests();
        
        // Close modal after delay
        setTimeout(() => {
          setShowJoinModal(false);
          setSelectedClub(null);
          setHasPlayedAsGuest(null);
          setGuestAccounts([]);
          setSelectedGuestId(null);
          setJoinSuccess(null);
        }, 2000);
      } else {
        throw new Error('Failed to link guest account');
      }
    } catch (err) {
      console.error('Error linking guest account:', err);
      setJoinError(err.message || 'Failed to link guest account. Please try again.');
    } finally {
      setMergingGuest(false);
    }
  };

  const handleStartFresh = async () => {
    if (!selectedGuestId) {
      setJoinError('Please select a guest account.');
      return;
    }

    try {
      setMergingGuest(true);
      setJoinError(null);
      setShowStartFreshModal(false);

      const result = await startFreshWithoutGuestMerge(
        selectedGuestId,
        currentUser.id,
        selectedClub.id
      );

      if (result.status === 'start_fresh' && result.club_joined) {
        setJoinSuccess('Started fresh membership! You are now a member of the club.');
        // Refresh data
        await fetchUserMemberships();
        await fetchUserJoinRequests();
        
        // Close modal after delay
        setTimeout(() => {
          setShowJoinModal(false);
          setSelectedClub(null);
          setHasPlayedAsGuest(null);
          setGuestAccounts([]);
          setSelectedGuestId(null);
          setJoinSuccess(null);
        }, 2000);
      } else {
        throw new Error('Failed to start fresh');
      }
    } catch (err) {
      console.error('Error starting fresh:', err);
      setJoinError(err.message || 'Failed to start fresh. Please try again.');
    } finally {
      setMergingGuest(false);
    }
  };

  const handleDecline = async (userMessage) => {
    if (!selectedGuestId) {
      setJoinError('Please select a guest account.');
      return;
    }

    try {
      setMergingGuest(true);
      setJoinError(null);
      setShowDeclineModal(false);

      const result = await declineGuestMergeWithReport(
        selectedGuestId,
        currentUser.id,
        selectedClub.id,
        userMessage
      );

      if (result.status === 'declined') {
        setJoinSuccess('Report sent! The club admin has been notified and will correct the email assignment. You\'ll receive a new notification once it\'s fixed.');
        
        // Close modal after delay
        setTimeout(() => {
          setShowJoinModal(false);
          setSelectedClub(null);
          setHasPlayedAsGuest(null);
          setGuestAccounts([]);
          setSelectedGuestId(null);
          setJoinSuccess(null);
        }, 3000);
      } else {
        throw new Error('Failed to decline merge');
      }
    } catch (err) {
      console.error('Error declining merge:', err);
      setJoinError(err.message || 'Failed to decline merge. Please try again.');
    } finally {
      setMergingGuest(false);
    }
  };

  const handleJoinRequest = async () => {
    if (!currentUser?.id || !selectedClub?.id) {
      setJoinError('Please log in to join a club.');
      return;
    }

    try {
      setSubmitting(true);
      setJoinError(null);
      setJoinSuccess(null);

      // Use the new RPC function for validation and creation
      const { data, error: joinError } = await supabase
        .rpc('create_join_request', {
          p_player_id: currentUser.id,
          p_club_id: selectedClub.id,
          p_message: joinMessage.trim() || null
        });

      if (joinError) throw joinError;

      if (data?.status === 'error') {
        throw new Error(data.error || 'Failed to create join request');
      }

      setJoinSuccess('Join request sent successfully!');
      setJoinMessage('');

      if (data?.request_id) {
        setUserJoinRequests((prev) => ({ ...prev, [selectedClub.id]: 'pending' }));
        setUserJoinRequestIds((prev) => ({ ...prev, [selectedClub.id]: data.request_id }));
      }
      
      // Refresh data
      await fetchUserJoinRequests();
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowJoinModal(false);
        setSelectedClub(null);
        setJoinSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Error sending join request:', err);
      setJoinError(err.message || 'Failed to send join request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawRequest = async (clubId) => {
    const requestId = userJoinRequestIds[clubId];
    if (!requestId || !currentUser?.id) {
      setJoinError('Unable to withdraw request.');
      return;
    }

    try {
      setWithdrawingRequest(clubId);
      setJoinError(null);
      setJoinSuccess(null);

      const { data, error } = await supabase
        .rpc('withdraw_join_request', {
          p_request_id: requestId,
          p_player_id: currentUser.id
        });

      if (error) throw error;

      if (data?.status === 'error') {
        throw new Error(data.error || 'Failed to withdraw request');
      }

      setJoinSuccess('Join request withdrawn successfully!');

      setUserJoinRequests((prev) => {
        const next = { ...prev };
        delete next[clubId];
        return next;
      });
      setUserJoinRequestIds((prev) => {
        const next = { ...prev };
        delete next[clubId];
        return next;
      });
      
      // Refresh data
      await fetchUserJoinRequests();
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowJoinModal(false);
        setSelectedClub(null);
        setJoinSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Error withdrawing join request:', err);
      setJoinError(err.message || 'Failed to withdraw join request.');
    } finally {
      setWithdrawingRequest(null);
    }
  };

  const patchInactiveMembership = (clubId, reactivationRequestedAt) => {
    setUserMemberships((prev) => ({
      ...prev,
      [clubId]: {
        ...prev[clubId],
        isActive: false,
        membershipStatus: 'inactive',
        reactivationRequestedAt,
      },
    }));
    setMemberClubs((prev) =>
      prev.map((club) =>
        club.id === clubId ? { ...club, reactivationRequestedAt } : club
      )
    );
  };

  const handleRequestReactivate = async (clubId) => {
    if (!currentUser?.id || !clubId) return;
    try {
      setWithdrawingRequest(clubId);
      await requestClubReactivation(clubId);
      patchInactiveMembership(clubId, new Date().toISOString());
      addToast({ message: 'Reactivation request sent to club admins.', variant: 'success' });
    } catch (err) {
      console.error('Error requesting club reactivation:', err);
      addToast({
        message: err.message || 'Failed to request reactivation.',
        variant: 'error',
      });
    } finally {
      setWithdrawingRequest(null);
    }
  };

  const handleWithdrawReactivate = async (clubId) => {
    if (!currentUser?.id || !clubId) return;
    try {
      setWithdrawingRequest(clubId);
      await withdrawClubReactivation(clubId);
      patchInactiveMembership(clubId, null);
      addToast({ message: 'Reactivation request withdrawn.', variant: 'success' });
    } catch (err) {
      console.error('Error withdrawing club reactivation:', err);
      addToast({
        message: err.message || 'Failed to withdraw reactivation request.',
        variant: 'error',
      });
    } finally {
      setWithdrawingRequest(null);
    }
  };

  // Filter clubs based on search term
  const filteredClubs = clubs.filter(club =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    club.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    club.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const clubMetaById = useMemo(
    () => Object.fromEntries(clubs.map((club) => [club.id, club])),
    [clubs]
  );

  const filteredMemberClubs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return memberClubs;
    return memberClubs.filter(
      (club) =>
        club.name?.toLowerCase().includes(q) ||
        club.location?.toLowerCase().includes(q)
    );
  }, [memberClubs, searchTerm]);

  const discoverClubs = useMemo(() => {
    if (!currentUser?.id) return filteredClubs;
    return filteredClubs.filter((club) => getUserClubStatus(club.id)?.status !== 'member');
  }, [filteredClubs, currentUser?.id, userMemberships, userJoinRequests, userJoinRequestCooldowns, userEmailAssignmentRequests]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState message="Loading clubs…" size="large" />
      </div>
    );
  }

  if (error && clubs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Heading level={3} className="mb-4 text-danger">Error</Heading>
          <Text className="mb-4 text-text-secondary">{error}</Text>
          <BackButton onClick={() => navigate('/')}>
            Back to Home
          </BackButton>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background ${APP_PAGE_BLEED_ROOT}`}>
      <div className={`max-w-app-shell mx-auto ${APP_PAGE_GUTTER} pt-3 md:pt-6 pb-8`}>
        {/* Back Button */}
        <div className="mb-3 sm:mb-6">
          <BackButton onClick={() => navigate('/')}>
            Back to Home
          </BackButton>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <Heading level={1} className="text-display mb-2">
            Clubs
          </Heading>
          <Text className="text-body-large text-text-secondary">
            {currentUser ? 'Your clubs and discover new ones' : 'Discover and explore badminton clubs'}
          </Text>
        </div>

        {/* Search Bar + Results Count */}
        {clubs.length > 0 && (
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="relative md:flex-1">
                <MobileSearchField
                  showIcon
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search clubs by name, location, or description..."
                  aria-label="Search clubs"
                  className={`form-input w-full ${searchTerm ? '!pr-10' : ''}`}
                />
                {searchTerm ? (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 min-h-[44px] min-w-[44px] -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors touch-manipulation sm:min-h-0 sm:min-w-0"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
              <div className="text-sm text-text-muted md:text-right md:whitespace-nowrap">
                Showing {filteredClubs.length} of {clubs.length} clubs
              </div>
            </div>
          </div>
        )}

        {/* Your Clubs */}
        {currentUser?.id && (memberClubsLoading || filteredMemberClubs.length > 0) ? (
          <div className="mb-12">
            <div className="mb-1">
              <Heading level={2} className="text-xl font-bold text-text-primary">
                Your clubs
              </Heading>
            </div>
            <Typography variant="caption" className="text-text-secondary block mb-5">
              Recently played
            </Typography>

            {memberClubsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[0, 1, 2].map((key) => (
                  <Card key={key} variant="default" className="h-52 animate-pulse bg-surface-subtle/60" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMemberClubs.map((club) => {
                  const meta = clubMetaById[club.id] || {};
                  const isInactive = club.membershipStatus === 'inactive';
                  const inactiveStatus = isInactive
                    ? {
                        status: 'inactive',
                        reactivationRequested: Boolean(club.reactivationRequestedAt),
                      }
                    : null;
                  return (
                    <ClubCard
                      key={club.id}
                      variant="member"
                      layout="compact"
                      insightsLoading={isInactive ? false : memberInsightsLoading}
                      club={{
                        id: club.id,
                        name: club.name,
                        location: club.location,
                        banner_image_url: club.bannerImageUrl,
                        logo_image_url: club.logoImageUrl || null,
                        role: club.role,
                        membershipStatus: club.membershipStatus,
                      }}
                      clubWinRatePct={isInactive ? null : club.clubWinRatePct}
                      memberCount={meta.member_count ?? null}
                      admins={meta.admins || []}
                      showClubMeta
                      membershipStatus={inactiveStatus}
                      onClick={isInactive ? null : () => navigate(`/clubs/${club.id}`)}
                      onViewClub={isInactive ? () => navigate(`/clubs/${club.id}`) : null}
                      onRequestReactivate={isInactive ? () => handleRequestReactivate(club.id) : null}
                      onWithdrawReactivate={isInactive ? () => handleWithdrawReactivate(club.id) : null}
                      isWithdrawing={withdrawingRequest === club.id}
                    />
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        {/* Discover Clubs */}
        {discoverClubs.length > 0 ? (
          <div className="mb-12">
            {currentUser?.id && filteredMemberClubs.length > 0 ? (
              <div className="mb-5">
                <Heading level={2} className="text-xl font-bold text-text-primary">
                  Discover clubs
                </Heading>
                <Typography variant="caption" className="text-text-secondary block mt-1">
                  Join new clubs, or request to reactivate an inactive membership
                </Typography>
              </div>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {discoverClubs.map((club) => {
                const membershipStatus = currentUser ? getUserClubStatus(club.id) : null;
                const canJoin = currentUser && membershipStatus?.status === 'can_join';
                const isInactive = membershipStatus?.status === 'inactive';

                return (
                  <ClubCard
                    key={club.id}
                    variant="public"
                    club={club}
                    onViewClub={
                      isInactive
                        ? () => navigate(`/clubs/${club.id}`)
                        : () => handleViewClub(club)
                    }
                    onRequestJoin={canJoin ? () => handleRequestJoin(club) : null}
                    onWithdraw={(membershipStatus?.status === 'pending' || membershipStatus?.status === 'rejected') ? () => handleWithdrawRequest(club.id) : null}
                    onRequestReactivate={isInactive ? () => handleRequestReactivate(club.id) : null}
                    onWithdrawReactivate={isInactive ? () => handleWithdrawReactivate(club.id) : null}
                    membershipStatus={membershipStatus}
                    hasPendingEmailRequest={userEmailAssignmentRequests[club.id] || false}
                    isWithdrawing={withdrawingRequest === club.id}
                    activity={clubActivity[club.id]}
                  />
                );
              })}
            </div>
          </div>
        ) : filteredClubs.length > 0 && currentUser?.id && filteredMemberClubs.length > 0 ? (
          <Card variant="default" size="large" className="text-center mb-12">
            <Heading level={3} className="mb-2">No other clubs to discover</Heading>
            <Text className="text-text-secondary">
              {searchTerm
                ? `No discoverable clubs match "${searchTerm}".`
                : 'You are already a member of all listed clubs.'}
            </Text>
          </Card>
        ) : null}

        {/* Empty State */}
        {filteredClubs.length === 0 ? (
          <Card variant="default" size="large" className="text-center mb-12">
            <div className="text-6xl mb-6">🔍</div>
            <Heading level={3} className="mb-4">
              {clubs.length === 0 ? 'No Clubs Available' : 'No Clubs Found'}
            </Heading>
            <Text className="mb-8 max-w-md mx-auto text-text-secondary">
              {clubs.length === 0 
                ? "There are no public clubs available at the moment."
                : `No clubs match "${searchTerm}". Try a different search term.`}
            </Text>
            {searchTerm && (
              <Button
                onClick={() => setSearchTerm('')}
                variant="outline"
                size="large"
              >
                Clear Search
              </Button>
            )}
          </Card>
        ) : null}

        {/* Join Club Modal */}
        {selectedClub && (
        <Modal
          isOpen={showJoinModal}
          onClose={resetJoinModalState}
        >
          <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <Card variant="default" size="medium" className="border-0 bg-transparent p-0 shadow-none">
              <div className="flex items-center justify-between mb-4">
                <div>
                <Heading level={3}>Join {selectedClub.name}</Heading>
                </div>
                <button
                  onClick={() => {
                    setShowJoinModal(false);
                    setSelectedClub(null);
                    setHasPlayedAsGuest(null);
                    setGuestAccounts([]);
                    setSelectedGuestId(null);
                    setNotificationMergeGuestId(null);
                    setJoinMessage('');
                    setJoinError(null);
                    setJoinSuccess(null);
                  }}
                  className="text-text-muted hover:text-text-primary transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {joinError && (
                <Card variant="danger" size="small" className="mb-4">
                  <Text className="text-danger text-sm">{joinError}</Text>
                </Card>
              )}
              
              {joinSuccess && (
                <Card variant="success" size="small" className="mb-4">
                  <Text className="text-green-600 text-sm">{joinSuccess}</Text>
                </Card>
              )}

              {/* Loading state while checking for guest accounts */}
              {hasPlayedAsGuest === null && loadingGuests && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <Text className="text-text-secondary">Checking for guest accounts...</Text>
                </div>
              )}

              {/* Identity Gate */}
              {hasPlayedAsGuest === null && !loadingGuests && (
                <>
                  <Text className="mb-6 font-medium text-text-primary">
                    Have you played at this club before?
                  </Text>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      onClick={() => handleIdentityGateAnswer(true)}
                      variant="outline"
                      className="flex-1 border-2 border-success text-success-dark hover:bg-success-light hover:border-success-dark"
                    >
                      Yes, I&apos;ve played before
                    </Button>
                    <Button
                      onClick={() => handleIdentityGateAnswer(false)}
                      variant="outline"
                      className="flex-1 border-2 border-border-medium text-text-secondary hover:bg-surface-subtle"
                    >
                      No, I haven&apos;t
                    </Button>
                  </div>
                </>
              )}

              {/* Guest Account Selection - Step 2: If yes, show guest accounts */}
              {hasPlayedAsGuest === true && !joinSuccess && (
                <>
                  {loadingGuests ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                      <Text className="text-text-secondary">Checking for guest accounts...</Text>
                    </div>
                  ) : guestAccounts.length > 0 ? (
                    <>
                      {notificationMergeGuestId ? (
                        <Text className="mb-4">
                          Your email has been assigned to a guest account at {selectedClub.name}. Review the details below and choose how you&apos;d like to proceed:
                        </Text>
                      ) : guestAccounts.length === 1 ? (
                        <Text className="mb-4 text-sm text-text-secondary">
                          We found your guest account at {selectedClub.name}:{' '}
                          <span className="font-medium text-text-primary">
                            {guestAccounts[0].first_name} {guestAccounts[0].last_name || ''}
                          </span>
                          .
                        </Text>
                      ) : (
                        <Text className="mb-4">
                          We found {guestAccounts.length} guest accounts at this club. Please select the one that belongs to you:
                        </Text>
                      )}
                      <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                        {guestAccounts.map((guest) => {
                          const hasDetails = guest.last_game || guest.mixed_elo || guest.levels_elo || guest.doubles_elo || guest.singles_elo;
                          const isExpanded = expandedGuests[guest.id] || false;
                          const isSingleGuest = guestAccounts.length === 1;
                          const isSelected = selectedGuestId === guest.id;

                          return (
                            <div
                            key={guest.id}
                              className={`w-full rounded-lg transition-colors ${
                              isSingleGuest
                                ? 'border border-primary/30 bg-primary/5'
                                : isSelected
                                  ? 'border-2 border-info bg-info-light'
                                  : 'border-2 border-border hover:border-border-medium'
                            }`}
                          >
                              <div className={isSingleGuest ? 'p-3' : 'p-4'}>
                                <div className="flex items-center justify-between">
                                  {isSingleGuest ? (
                                    <div className="flex-1 text-left">
                                      <div className="font-medium text-text-primary">
                                        {guest.first_name} {guest.last_name || ''}
                                      </div>
                                    </div>
                                  ) : (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedGuestId(guest.id)}
                                    className="flex-1 text-left"
                                  >
                                    <div className="font-medium text-text-primary">
                                      {guest.first_name} {guest.last_name || ''}
                                    </div>
                                  </button>
                                  )}
                                  {hasDetails && (
                                    <button
                                      type="button"
                                      onClick={() => setExpandedGuests(prev => ({
                                        ...prev,
                                        [guest.id]: !prev[guest.id]
                                      }))}
                                      className="ml-2 text-text-muted hover:text-text-primary flex-shrink-0"
                                    >
                                      <svg
                                        className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                
                                {/* Basic Stats - Always Visible */}
                                <div className={`grid grid-cols-2 gap-2 mt-2 ${isSingleGuest ? 'text-xs' : 'text-sm'}`}>
                                  <div className="text-text-secondary">
                                    <span className="font-medium text-text-primary">{guest.game_count || 0}</span> game{guest.game_count !== 1 ? 's' : ''}
                                  </div>
                                  <div className="text-text-secondary">
                                    <span className="font-medium text-text-primary">{guest.total_sessions || 0}</span> session{guest.total_sessions !== 1 ? 's' : ''}
                                  </div>
                              {guest.first_game_date && (
                                    <div className="text-text-muted col-span-2 text-xs">
                                      {new Date(guest.first_game_date).toLocaleDateString()} - {new Date(guest.last_game_date).toLocaleDateString()}
                                    </div>
                              )}
                            </div>
                              </div>
                              
                              {/* Collapsible Details */}
                              {hasDetails && isExpanded && (
                                <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                                  {/* Last Game */}
                                  {guest.last_game && (
                                    <div className="bg-surface-subtle rounded-lg p-3">
                                      <div className="text-xs text-text-muted mb-2 font-medium">Last Game</div>
                                      
                                      {/* Result and Score */}
                                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          guest.last_game.result === 'Win' 
                                            ? 'bg-success-light text-success-dark' 
                                            : 'bg-danger-light text-danger-dark'
                                        }`}>
                                          {guest.last_game.result}
                                        </span>
                                        {guest.last_game.score && (
                                          <span className="text-text-primary font-semibold">{guest.last_game.score}</span>
                                        )}
                                        {guest.last_game.date && (
                                          <span className="text-text-muted text-xs">
                                            {new Date(guest.last_game.date).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Session Name */}
                                        {guest.last_game.sessionName && (
                                        <div className="text-xs text-text-secondary mb-3">
                                          {guest.last_game.sessionName}
                                        </div>
                                      )}
                                      
                                      {/* Teams Side-by-Side Layout */}
                                      <div className="flex items-center justify-center gap-3 flex-wrap">
                                        {/* Team 1 (Guest's Team) - Left */}
                                        <div className="flex gap-2">
                                          <span className="px-2 py-1 bg-team1-light text-team1 rounded text-xs font-medium">
                                            {guest.first_name}
                                          </span>
                                          {guest.last_game.teammates && guest.last_game.teammates.length > 0 && (
                                            guest.last_game.teammates.map((teammate, idx) => (
                                              <span key={idx} className="px-2 py-1 bg-team1-light text-team1 rounded text-xs font-medium">
                                                {teammate}
                                              </span>
                                            ))
                                          )}
                                        </div>
                                        
                                        {/* VS */}
                                        <div className="text-xs font-medium text-text-muted">vs</div>
                                        
                                        {/* Team 2 (Opponents) - Right */}
                                        {guest.last_game.opponents && guest.last_game.opponents.length > 0 && (
                                          <div className="flex gap-2">
                                            {guest.last_game.opponents.map((opponent, idx) => (
                                              <span key={idx} className="px-2 py-1 bg-team2-light text-team2 rounded text-xs font-medium">
                                                {opponent}
                                              </span>
                        ))}
                      </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* ELO Ratings */}
                                  {(guest.mixed_elo || guest.levels_elo || guest.doubles_elo || guest.singles_elo) && (
                                    <div>
                                      <div className="text-xs text-text-muted mb-1 font-medium">ELO Ratings:</div>
                                      <div className="flex flex-wrap gap-2 text-xs">
                                        {guest.mixed_elo && (
                                          <span className="px-2 py-0.5 bg-primary/15 text-primary rounded">
                                            Mixed: {guest.mixed_elo}
                                          </span>
                                        )}
                                        {guest.levels_elo && (
                                          <span className="px-2 py-0.5 bg-info-light text-info rounded">
                                            Levels: {guest.levels_elo}
                                          </span>
                                        )}
                                        {guest.doubles_elo && (
                                          <span className="px-2 py-0.5 bg-success-light text-success-dark rounded">
                                            Doubles: {guest.doubles_elo}
                                          </span>
                                        )}
                                        {guest.singles_elo && (
                                          <span className="px-2 py-0.5 bg-warning-light text-warning-dark rounded">
                                            Singles: {guest.singles_elo}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Separator */}
                      <div className="my-3 border-t border-border-light" />
                      
                      {/* Selection Prompt — only when multiple guests need a pick */}
                      {guestAccounts.length > 1 && !selectedGuestId && (
                        <div className="mb-3 p-3 bg-warning-light border border-warning/40 rounded-lg">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-warning-dark">
                              <span className="font-medium">Please select a guest account above</span> to choose how you'd like to proceed.
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <GuestLinkActionChooser
                        disabled={!selectedGuestId}
                        loading={mergingGuest}
                        onMerge={() => setShowMergeConfirmModal(true)}
                        onStartFresh={() => setShowStartFreshModal(true)}
                        onDecline={() => setShowDeclineModal(true)}
                      />
                    </>
                  ) : (
                    <>
                      <div className="mb-4 flex items-center gap-2">
                        <Text className="font-medium text-text-primary">
                          Link your guest account
                        </Text>
                        <ModalInfoTooltip
                          text={`We couldn't find a guest account linked to your email at ${selectedClub.name} yet. Request a link and join — the club admin will connect your past games and ratings, then notify you when it's ready.`}
                        />
                      </div>
                      <Button
                        onClick={handleSendEmailAssignmentRequest}
                        variant="primary"
                        className="w-full"
                        disabled={sendingRequest || userEmailAssignmentRequests[selectedClub.id]}
                      >
                        {sendingRequest
                          ? 'Sending Request...'
                          : userEmailAssignmentRequests[selectedClub.id]
                            ? 'Request Already Sent'
                            : 'Request Account Link & Join'}
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          setHasPlayedAsGuest(null);
                          setJoinError(null);
                        }}
                        className="mt-3 w-full text-sm text-text-muted hover:text-text-secondary"
                        disabled={sendingRequest}
                      >
                        ← Back
                      </button>
                    </>
                  )}
                </>
              )}

              {hasPlayedAsGuest === false && loadingGuests && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <Text className="text-text-secondary">Checking for guest accounts...</Text>
                </div>
              )}

              {hasPlayedAsGuest === false && !loadingGuests && !joinSuccess && guestNameHints.length > 0 && !dismissedGuestNameHint && (
                <>
                  <Text className="mb-2 font-medium text-text-primary">
                    It looks like a guest account may exist
                  </Text>
                  <Text className="mb-4 text-sm text-text-secondary">
                    A past walk-in record at {selectedClub.name} has a similar name. This is not a full history.
                  </Text>
                  <div className="mb-4 space-y-2">
                    {guestNameHints.map((hint, index) => (
                      <div
                        key={`${hint.display_name}-${index}`}
                        className="rounded-lg border border-warning/40 bg-warning-light px-3 py-2.5"
                      >
                        <Text className="font-medium text-text-primary">{hint.display_name}</Text>
                        <Text className="text-sm text-text-secondary">{hint.activity_label}</Text>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => handleIdentityGateAnswer(true)}
                      variant="primary"
                      className="w-full"
                    >
                      That might be me
                    </Button>
                    <Button
                      onClick={() => setDismissedGuestNameHint(true)}
                      variant="outline"
                      className="w-full"
                    >
                      That&apos;s not me
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setHasPlayedAsGuest(null);
                      setGuestNameHints([]);
                      setDismissedGuestNameHint(false);
                      setJoinError(null);
                    }}
                    className="mt-3 w-full text-sm text-text-muted hover:text-text-secondary"
                  >
                    ← Back
                  </button>
                </>
              )}

              {/* First-time join request */}
              {hasPlayedAsGuest === false && !loadingGuests && !joinSuccess && (guestNameHints.length === 0 || dismissedGuestNameHint) && (
                <>
                  <Text className="mb-3 font-medium text-text-primary">
                    First time joining?
                  </Text>
                  <Text className="mb-4 text-sm text-text-secondary">
                    Send a request to join {selectedClub.name}. A club admin will review it.
                  </Text>

                  <textarea
                    value={joinMessage}
                    onChange={(e) => setJoinMessage(e.target.value)}
                    placeholder="Optional message to club admin..."
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                    rows={4}
                    disabled={submitting}
                  />
                  <Button
                    onClick={handleJoinRequest}
                    variant="primary"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Request to Join Club'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setHasPlayedAsGuest(null);
                      setGuestNameHints([]);
                      setDismissedGuestNameHint(false);
                      setJoinError(null);
                    }}
                    className="mt-3 w-full text-sm text-text-muted hover:text-text-secondary"
                    disabled={submitting}
                  >
                    ← Back
                  </button>
                </>
              )}

            </Card>
            </div>
          </ModalContent>
        </Modal>
        )}

        {/* Club Info Modal */}
        <ClubInfoModal
          club={selectedClub}
          isOpen={showClubInfoModal}
          onClose={() => {
            setShowClubInfoModal(false);
            setSelectedClub(null);
          }}
        />

        {/* Start Fresh Warning Modal */}
        {selectedGuestId && selectedClub && (() => {
          const selectedGuest = guestAccounts.find(g => g.id === selectedGuestId);
          const guestName = selectedGuest ? `${selectedGuest.first_name} ${selectedGuest.last_name || ''}`.trim() : 'this guest';
          return (
            <StartFreshWarningModal
              isOpen={showStartFreshModal}
              onClose={() => setShowStartFreshModal(false)}
              onConfirm={handleStartFresh}
              guestName={guestName}
              clubName={selectedClub.name}
            />
          );
        })()}

        {/* Decline/Report Modal */}
        {selectedGuestId && selectedClub && (() => {
          const selectedGuest = guestAccounts.find(g => g.id === selectedGuestId);
          const guestName = selectedGuest ? `${selectedGuest.first_name} ${selectedGuest.last_name || ''}`.trim() : 'this guest';
          return (
            <DeclineGuestMergeModal
              isOpen={showDeclineModal}
              onClose={() => setShowDeclineModal(false)}
              onConfirm={handleDecline}
              guestName={guestName}
              clubName={selectedClub.name}
            />
          );
        })()}

        {/* Merge & Join Confirmation Modal */}
        {selectedGuestId && selectedClub && (() => {
          const selectedGuest = guestAccounts.find(g => g.id === selectedGuestId);
          const guestName = selectedGuest ? `${selectedGuest.first_name} ${selectedGuest.last_name || ''}`.trim() : 'this guest';
          const gameCount = selectedGuest?.game_count || 0;
          return (
            <ConfirmationModal
              isOpen={showMergeConfirmModal}
              onClose={() => setShowMergeConfirmModal(false)}
              onConfirm={() => {
                setShowMergeConfirmModal(false);
                handleGuestSelectionAndMerge();
              }}
              title="Confirm Merge & Join"
              message={`You are about to merge your guest account "${guestName}" (${gameCount} game${gameCount !== 1 ? 's' : ''}) into your user account and join ${selectedClub.name}. This will preserve all your match history, ELO ratings, and partnerships. This action cannot be undone.`}
              confirmText="Yes, Merge & Join"
              cancelText="Cancel"
              isDangerous={false}
            />
          );
        })()}

      </div>
    </div>
  );
};

export default Clubs;
